import { Readability } from '@mozilla/readability';
import { supabase } from '@/integrations/supabase/client';

export interface ExtractedArticle {
  title: string;
  description: string;
  content: string;
  textContent: string;
  byline?: string;
  siteName?: string;
  lang?: string;
  publishedTime?: string;
  leadImageUrl?: string;
  sourceUrl: string;
}

interface ExtractArticleEdgeResponse {
  html: string;
  url: string;
  finalUrl: string;
  leadImageUrl?: string;
  siteName?: string;
  contentType?: string;
  error?: string;
}

/**
 * Cleans up text extracted by Readability:
 * - Collapses sequences of 3+ blank lines into 2
 * - Trims leading / trailing whitespace per line
 * - Removes stray non-breaking spaces
 */
const normalizeExtractedText = (raw: string): string => {
  return raw
    .replace(/\u00a0/g, ' ')
    .split(/\r?\n/)
    .map(line => line.replace(/[\t ]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/**
 * Calls the `extract-article` Edge Function, then parses the returned HTML
 * with Mozilla Readability in the browser to produce a clean article payload.
 *
 * The Edge Function handles the cross-origin fetch (no CORS in the browser);
 * Readability runs client-side where a native `Document` is available.
 */
export const extractArticleFromUrl = async (
  url: string,
  userId?: string
): Promise<ExtractedArticle> => {
  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    throw new Error('Please provide a URL.');
  }

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(trimmedUrl);
  } catch {
    throw new Error('The URL you entered is not valid.');
  }
  if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
    throw new Error('Only http(s) URLs are supported.');
  }

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    throw new Error(
      'Article extraction is only available in the browser environment.'
    );
  }

  const headers: Record<string, string> = {};
  if (userId) {
    headers['x-clerk-user-id'] = userId;
  }

  const { data, error } =
    await supabase.functions.invoke<ExtractArticleEdgeResponse>(
      'extract-article',
      {
        body: { url: trimmedUrl },
        headers,
      }
    );

  if (error) {
    throw new Error(`Failed to fetch article: ${error.message}`);
  }
  if (!data || !data.html) {
    throw new Error(data?.error ?? 'Empty response from extract-article.');
  }

  const doc = new DOMParser().parseFromString(data.html, 'text/html');

  // Resolve relative URLs inside the document by injecting a <base> tag.
  // Readability uses the document baseURI when normalizing links/images.
  if (doc.head && !doc.querySelector('base')) {
    const base = doc.createElement('base');
    base.setAttribute('href', data.finalUrl || trimmedUrl);
    doc.head.prepend(base);
  }

  const reader = new Readability(doc, { keepClasses: false });
  const parsed = reader.parse();

  if (!parsed) {
    throw new Error(
      'Could not extract an article from this page. Please paste the text manually.'
    );
  }

  const title = (parsed.title ?? '').trim();
  const textContent = normalizeExtractedText(parsed.textContent ?? '');
  const excerpt = (parsed.excerpt ?? '').trim();

  if (!textContent) {
    throw new Error(
      'The extractor could not find any article text on this page.'
    );
  }

  return {
    title,
    description: excerpt,
    content: parsed.content ?? '',
    textContent,
    byline: parsed.byline ?? undefined,
    siteName: parsed.siteName ?? data.siteName ?? undefined,
    lang: parsed.lang ?? undefined,
    publishedTime: parsed.publishedTime ?? undefined,
    leadImageUrl: data.leadImageUrl,
    sourceUrl: data.finalUrl || trimmedUrl,
  };
};
